import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { getDateLocale } from '../../utils/dateLocale';
import { Send, Loader2, Mail, Search, Users, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import api from '../../services/api';

export const CorrespondancesSection = ({
  events, correspondances, corrLoading, showNewCorr, setShowNewCorr,
  corrForm, setCorrForm, corrSending, onSend
}) => {
  // 'all' = broadcast to all registrants; 'manual' = pick specific registrants
  const [mode, setMode] = useState('all');
  const [registrations, setRegistrations] = useState([]);
  const [historicals, setHistoricals] = useState([]);
  const [regLoading, setRegLoading] = useState(false);
  const [regSearch, setRegSearch] = useState('');
  const [raceFilter, setRaceFilter] = useState('');
  const [includeHistorical, setIncludeHistorical] = useState(true);

  // Sync corrForm.recipient_ids with mode
  useEffect(() => {
    if (mode === 'all') {
      setCorrForm(p => ({ ...p, recipient_ids: 'all' }));
    } else {
      setCorrForm(p => ({ ...p, recipient_ids: Array.isArray(p.recipient_ids) ? p.recipient_ids : [] }));
    }
  }, [mode, setCorrForm]);

  // Fetch registrations + historical participants whenever the selected event changes (in manual mode)
  useEffect(() => {
    const evId = corrForm.event_id;
    if (mode !== 'manual' || !evId || evId === 'all') { setRegistrations([]); setHistoricals([]); return; }
    let cancelled = false;
    (async () => {
      setRegLoading(true);
      try {
        const [regsRes, histRes] = await Promise.all([
          api.get(`/organizer/registrations/${evId}`).catch(() => ({ data: { registrations: [] } })),
          api.get('/organizer/historical-participants').catch(() => ({ data: { participants: [] } })),
        ]);
        if (!cancelled) {
          setRegistrations(regsRes.data.registrations || []);
          setHistoricals(histRes.data.participants || []);
        }
      } catch { if (!cancelled) { setRegistrations([]); setHistoricals([]); } }
      finally { if (!cancelled) setRegLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [mode, corrForm.event_id]);

  // Merge registrations + historical participants into a single contact list
  const allContacts = useMemo(() => {
    const fromRegs = registrations.map(r => ({
      id: r.registration_id,
      first_name: r.first_name || '',
      last_name: r.last_name || '',
      user_name: r.user_name || `${r.first_name || ''} ${r.last_name || ''}`.trim(),
      email: r.email || r.user_email || '',
      race: r.selected_race || '',
      bib: r.bib_number || '',
      source: 'registered',
    }));
    if (!includeHistorical) return fromRegs;
    // Deduplicate historical by email vs existing registrations
    const existingEmails = new Set(fromRegs.map(r => (r.email || '').toLowerCase()).filter(Boolean));
    const fromHist = historicals
      .filter(h => !h.email || !existingEmails.has(h.email.toLowerCase()))
      .map(h => ({
        id: h.participant_id,
        first_name: h.first_name || '',
        last_name: h.last_name || '',
        user_name: `${h.first_name || ''} ${h.last_name || ''}`.trim() || 'Contact',
        email: h.email || '',
        race: h.race || h.event_name || '',
        bib: '',
        source: 'historical',
      }));
    return [...fromRegs, ...fromHist];
  }, [registrations, historicals, includeHistorical]);

  const races = useMemo(() => Array.from(new Set(allContacts.map(c => c.race).filter(Boolean))).sort(), [allContacts]);

  const filteredRegs = useMemo(() => {
    const s = regSearch.trim().toLowerCase();
    return allContacts.filter(c => {
      if (raceFilter && c.race !== raceFilter) return false;
      if (!s) return true;
      return (c.first_name || '').toLowerCase().includes(s)
        || (c.last_name || '').toLowerCase().includes(s)
        || (c.user_name || '').toLowerCase().includes(s)
        || (c.email || '').toLowerCase().includes(s);
    });
  }, [allContacts, regSearch, raceFilter]);

  const selectedIds = Array.isArray(corrForm.recipient_ids) ? corrForm.recipient_ids : [];

  const toggleRecipient = (regId) => {
    setCorrForm(p => {
      const cur = Array.isArray(p.recipient_ids) ? p.recipient_ids : [];
      return { ...p, recipient_ids: cur.includes(regId) ? cur.filter(x => x !== regId) : [...cur, regId] };
    });
  };

  const selectAllFiltered = () => {
    const ids = filteredRegs.map(c => c.id);
    setCorrForm(p => ({ ...p, recipient_ids: ids }));
  };
  const clearSelection = () => setCorrForm(p => ({ ...p, recipient_ids: [] }));

  // Reset transient UI state when closing
  useEffect(() => {
    if (!showNewCorr) { setMode('all'); setRegSearch(''); setRaceFilter(''); setIncludeHistorical(true); }
  }, [showNewCorr]);

  return (
  <div>
    <div className="flex justify-end mb-4">
      <Button className="btn-primary gap-2" onClick={() => setShowNewCorr(true)} data-testid="new-correspondance-btn">
        <Send className="w-4 h-4" /> Nouveau message
      </Button>
    </div>

    <AnimatePresence>
      {showNewCorr && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6 overflow-hidden">
          <div className="bg-white border border-slate-200 p-6">
            <h3 className="font-heading font-bold uppercase text-sm mb-4">Envoyer un message</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-xs font-heading uppercase text-slate-500">Evenement</Label>
                <Select value={corrForm.event_id} onValueChange={(v) => setCorrForm(p => ({ ...p, event_id: v }))}>
                  <SelectTrigger data-testid="corr-event-select"><SelectValue placeholder="Choisir un evenement" /></SelectTrigger>
                  <SelectContent>
                    {events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-heading uppercase text-slate-500">Destinataires</Label>
                <Select value={mode} onValueChange={(v) => setMode(v)}>
                  <SelectTrigger data-testid="corr-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les inscrits</SelectItem>
                    <SelectItem value="manual">Selection manuelle...</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Manual recipient picker */}
            {mode === 'manual' && (
              <div className="mb-4 border border-slate-200 bg-slate-50">
                <div className="p-3 border-b border-slate-200 flex flex-wrap gap-2 items-center bg-white">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                    <input type="text" value={regSearch} onChange={e => setRegSearch(e.target.value)}
                      placeholder="Rechercher par nom, email..."
                      className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-200"
                      data-testid="corr-recipient-search" />
                  </div>
                  {races.length > 0 && (
                    <select value={raceFilter} onChange={e => setRaceFilter(e.target.value)} className="border border-slate-200 px-2 py-1.5 text-xs" data-testid="corr-race-filter">
                      <option value="">Toutes les epreuves</option>
                      {races.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  )}
                  <label className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700 cursor-pointer px-2 py-1 border border-slate-200 bg-white hover:bg-slate-50" title="Inclure les contacts importes (Historique des participants)">
                    <input type="checkbox" checked={includeHistorical} onChange={e => setIncludeHistorical(e.target.checked)} className="w-3.5 h-3.5 accent-brand" data-testid="corr-include-historical" />
                    Contacts historiques
                  </label>
                  <button type="button" onClick={selectAllFiltered} className="px-2 py-1 text-xs bg-slate-800 text-white hover:bg-slate-700 font-bold" data-testid="corr-select-all">
                    Tout selectionner ({filteredRegs.length})
                  </button>
                  <button type="button" onClick={clearSelection} className="px-2 py-1 text-xs bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 font-bold" data-testid="corr-clear-selection">
                    Tout deselectionner
                  </button>
                  <span className="ml-auto inline-flex items-center gap-1 px-2 py-1 bg-brand text-white text-xs font-bold" data-testid="corr-selected-count">
                    <Users className="w-3 h-3" /> {selectedIds.length} selectionne(s)
                  </span>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {!corrForm.event_id || corrForm.event_id === 'all' ? (
                    <div className="p-8 text-center text-slate-400 text-sm">Selectionnez d'abord un evenement</div>
                  ) : regLoading ? (
                    <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-brand" /></div>
                  ) : filteredRegs.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm">Aucun destinataire{regSearch || raceFilter ? ' ne correspond aux filtres' : ''}</div>
                  ) : (
                    <ul className="divide-y divide-slate-200">
                      {filteredRegs.map(c => {
                        const checked = selectedIds.includes(c.id);
                        const name = c.user_name || `${c.first_name} ${c.last_name}`.trim() || 'Sans nom';
                        const isHist = c.source === 'historical';
                        return (
                          <li key={c.id}>
                            <label className={`flex items-center gap-3 p-2.5 cursor-pointer hover:bg-white ${checked ? 'bg-orange-50' : ''}`}>
                              <input type="checkbox" checked={checked} onChange={() => toggleRecipient(c.id)}
                                className="w-4 h-4 accent-brand" data-testid={`corr-recipient-${c.id}`} />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-bold truncate flex items-center gap-2">
                                  {name}
                                  <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase ${isHist ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                    {isHist ? 'Contact' : 'Inscrit'}
                                  </span>
                                </div>
                                <div className="text-xs text-slate-500 truncate">{c.email || '—'}{c.race ? ` · ${c.race}` : ''}{c.bib ? ` · #${c.bib}` : ''}</div>
                              </div>
                              {checked && <Check className="w-4 h-4 text-brand shrink-0" />}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            )}

            <div className="mb-4">
              <Label className="text-xs font-heading uppercase text-slate-500">Sujet</Label>
              <Input placeholder="Ex: Informations course, recuperation dossard..." value={corrForm.subject} onChange={(e) => setCorrForm(p => ({ ...p, subject: e.target.value }))} data-testid="corr-subject" />
            </div>
            <div className="mb-4">
              <Label className="text-xs font-heading uppercase text-slate-500">Message</Label>
              <Textarea rows={5} placeholder="Votre message aux participants..." value={corrForm.message} onChange={(e) => setCorrForm(p => ({ ...p, message: e.target.value }))} data-testid="corr-message" />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={corrForm.send_email} onChange={(e) => setCorrForm(p => ({ ...p, send_email: e.target.checked }))} className="w-4 h-4 accent-brand" data-testid="corr-send-email" />
                <span className="text-sm font-medium">Envoyer aussi par email</span>
                <Mail className="w-4 h-4 text-slate-400" />
              </label>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowNewCorr(false)}>Annuler</Button>
                <Button className="btn-primary gap-2" onClick={onSend}
                  disabled={corrSending || (mode === 'manual' && selectedIds.length === 0)}
                  data-testid="send-corr-btn">
                  {corrSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Envoyer{mode === 'manual' && selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>

    <div className="bg-white border border-slate-200">
      <div className="p-4 border-b"><h3 className="font-heading font-bold uppercase text-sm">Historique des envois</h3></div>
      {corrLoading ? (
        <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-brand" /></div>
      ) : correspondances.length > 0 ? (
        <div className="divide-y">
          {correspondances.map(c => (
            <div key={c.correspondance_id} className="p-4 hover:bg-slate-50">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-heading font-bold text-sm">{c.subject || 'Sans sujet'}</h4>
                <span className="text-xs text-slate-400">{c.created_at && format(new Date(c.created_at), 'd MMM yyyy HH:mm', { locale: getDateLocale() })}</span>
              </div>
              <p className="text-sm text-slate-600 line-clamp-2">{c.message}</p>
              <div className="flex gap-3 mt-2 text-xs text-slate-400">
                <span>{c.recipient_count} destinataire(s)</span>
                {c.send_email && <span className="text-brand">{c.email_sent_count} email(s) envoye(s)</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-8 text-center text-slate-400">Aucun message envoye</div>
      )}
    </div>
  </div>
  );
};
